import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { sendStockChat } from "../lib/api";
import { MessageCircle, Send, Loader2, X, Maximize2, Minimize2, Sparkles, Brain } from "lucide-react";

/**
 * StockChat — GPT-5.2 powered AI analyst for a given stock.
 * Modes:
 *   - inline (default): renders an inline card on the Stock Detail page (always visible).
 *   - floating: renders a fixed bottom-right floating launcher.
 * Both modes share the same conversation state when mounted.
 */
export default function StockChat({ symbol, mode = "inline" }) {
  const [floatingOpen, setFloatingOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${symbol}-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, floatingOpen]);

  const send = async (overrideMsg) => {
    const msg = (overrideMsg ?? input).trim();
    if (!msg || loading) return;
    if (!overrideMsg) setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await sendStockChat(symbol, msg, sessionId);
      setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setMessages(prev => [...prev, {
        role: "assistant",
        content: typeof detail === "string" ? detail : "AI request failed. Check the backend logs for the provider error.",
      }]);
    }
    setLoading(false);
  };

  const suggestions = [
    `What's the growth outlook for ${symbol}?`,
    `Is ${symbol} overvalued right now?`,
    `Key risks for ${symbol}?`,
    `Compare ${symbol} to its peers`,
  ];

  /* ─── Inline mode (Stock Detail page) ─── */
  if (mode === "inline") {
    return (
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg overflow-hidden" data-testid="stock-chat-inline">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-cyan-500/5 to-transparent">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-cyan-500/15 border border-cyan-500/25 flex items-center justify-center">
              <Brain size={14} className="text-cyan-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-white">Ask AI Analyst about {symbol}</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-mono">GPT-5.2</span>
              </div>
              <div className="text-[10px] text-[var(--text-muted)]">Get instant analysis, valuation opinions, risk breakdown</div>
            </div>
          </div>
          {messages.length > 0 && (
            <button
              onClick={() => { setMessages([]); setInput(""); }}
              className="text-[10px] text-[var(--text-muted)] hover:text-white transition-colors"
              data-testid="chat-clear"
            >
              Clear
            </button>
          )}
        </div>

        <div ref={scrollRef} className="max-h-[420px] min-h-[200px] overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                <Sparkles size={12} className="text-amber-400" />
                <span>Try a suggested question or ask your own:</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    disabled={loading}
                    className="text-left text-xs text-[var(--text-secondary)] bg-[var(--bg-base)] border border-white/5 rounded-lg px-3 py-2 hover:border-cyan-500/25 hover:text-white transition-colors disabled:opacity-40"
                    data-testid={`chat-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed ${m.role === "user" ? "bg-[var(--brand)] text-white" : "bg-[var(--bg-base)] border border-white/5 text-[var(--text-secondary)]"}`}>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-[var(--bg-base)] border border-white/5 rounded-lg px-3 py-2 flex items-center gap-2">
                <Loader2 size={12} className="animate-spin text-cyan-400" />
                <span className="text-[10px] text-[var(--text-muted)]">Analyzing...</span>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-white/10 p-3 bg-[var(--bg-base)]/30">
          <div className="flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
              placeholder={`Ask anything about ${symbol}...`}
              className="flex-1 bg-[var(--bg-base)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-cyan-500/40"
              data-testid="chat-input"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded-lg px-4 py-2 disabled:opacity-40 transition-colors flex items-center gap-1.5 text-xs font-medium"
              data-testid="chat-send"
            >
              <Send size={14} />
              Ask
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Floating mode (legacy) ─── */
  const chatContent = floatingOpen ? (
    <div className={`fixed z-[9999] bg-[var(--bg-surface)] border border-white/10 rounded-xl shadow-2xl flex flex-col transition-all duration-200 ${expanded ? "inset-4" : "bottom-6 right-6 w-[400px] h-[520px]"}`} data-testid="stock-chat">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle size={16} className="text-[var(--brand)]" />
          <span className="text-sm font-semibold text-white">{symbol} AI Chat</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">GPT-5.2</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setExpanded(!expanded)} className="text-[var(--text-muted)] hover:text-white p-1"><Minimize2 size={14} className={expanded ? "" : "hidden"} /><Maximize2 size={14} className={expanded ? "hidden" : ""} /></button>
          <button onClick={() => setFloatingOpen(false)} className="text-[var(--text-muted)] hover:text-white p-1" data-testid="chat-close"><X size={14} /></button>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="space-y-1.5">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => send(s)} className="w-full text-left text-xs text-[var(--text-secondary)] bg-[var(--bg-base)] border border-white/5 rounded-lg px-3 py-2 hover:border-white/15 hover:text-white transition-colors">{s}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed ${m.role === "user" ? "bg-[var(--brand)] text-white" : "bg-[var(--bg-base)] border border-white/5 text-[var(--text-secondary)]"}`}>
              <div className="whitespace-pre-wrap">{m.content}</div>
            </div>
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="bg-[var(--bg-base)] border border-white/5 rounded-lg px-3 py-2"><Loader2 size={14} className="animate-spin text-[var(--brand)]" /></div></div>}
      </div>
      <div className="flex-shrink-0 border-t border-white/10 p-3">
        <div className="flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()} placeholder={`Ask about ${symbol}...`} className="flex-1 bg-[var(--bg-base)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-cyan-500/40" />
          <button onClick={() => send()} disabled={!input.trim() || loading} className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded-lg px-3 py-2 disabled:opacity-40 transition-colors"><Send size={16} /></button>
        </div>
      </div>
    </div>
  ) : (
    <button onClick={() => setFloatingOpen(true)} className="fixed bottom-6 right-6 z-[9999] bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded-full px-4 py-3 shadow-xl transition-transform hover:scale-105 flex items-center gap-2" data-testid="chat-toggle">
      <MessageCircle size={18} />
      <span className="text-xs font-medium">Ask AI</span>
    </button>
  );

  return ReactDOM.createPortal(chatContent, document.body);
}
